import { externalApiClient } from "../api/axios";
import { Authorization, OrganizationsRef, User } from "../models/User";

// Shared by first-bind status + resend; only "pending" renders the OTP form, the rest are dead/used.
export type FirstBindState = "pending" | "verified" | "consumed" | "already_bound" | "expired";

// The send runs off the request thread, so a poll can land mid-flight — only "failed" is a real problem.
export type OtpDelivery = "sending" | "sent" | "failed";

export type FirstBindStatus = {
  fb_code: string;
  masked_email: string;
  code_expires_at: string;
  resend_available_at: string;
  attempts_remaining: number;
  resends_remaining: number;
  state: FirstBindState;
  otp_delivery?: OtpDelivery;
};

// Result of a verified first-bind OTP — redirectUrl carries the exchange handoff.
export type FirstBindVerifyResult = {
  handoff: {
    code: string;
    expiresIn: number;
    redirectUrl: string;
  };
};

// One sector option from GET /v1/app/sso/sectors (field names match the external-api contract exactly).
export type SectorOption = {
  // the parentRef that lists the codes under this sector
  id: string;
  code: string;
  nameEn: string;
  nameTh: string;
  level: number;
};

export type SectorTaxonomy = "tsic" | "isic";

export type OnboardingListingStatus = "listed" | "unlisted";

// The server folds not-an-admin, not-a-borrower and already-answered into "completed", so a caller cannot probe.
export type OnboardingStatus = "pending" | "completed";

// Named as the request keys, so a field the server lists is the key the answer carries.
export type OnboardingField = "sectorIsicCode" | "sectorTsicCode" | "listingStatus";

export type OnboardingPayload = {
  // The org's ObjectId, not its numeric id — only GET /v2/organizations ever hands the FE one.
  organizationRef: string;
  // Only the fields the server asked; one the bank already set is refused with 409.
  sectorIsicCode?: string;
  sectorTsicCode?: string;
  // No fiscalYear: onboarding must never overwrite the org's real fiscal year with Jan-Dec.
  listingStatus?: OnboardingListingStatus;
};

export type OnboardingResult = {
  onboarding_status: OnboardingStatus;
  // Present only while pending: what the bank left empty, and what may be answered alongside it.
  missing_fields?: OnboardingField[];
  optional_fields?: OnboardingField[];
};

// ตรงกับ SsoRegisterOrgSchema ที่ backend — nameEn/nameTh อย่างน้อยหนึ่ง, taxId 13 หลัก, industryType ใน whitelist.
export type LinkOrgInput = {
  nameEn?: string;
  nameTh?: string;
  taxId?: string;
  province?: string;
  industryType?: string;
};

// organization เป็น {} ถ้า link token ออกก่อน feature นี้ landed — caller ต้องเช็คก่อน prefill.
export type LinkPreview = {
  partner: { partnerId: string };
  organization: LinkOrgInput;
};

// FE ต้อง merge เข้า UserContext ก่อน navigate — ไม่งั้น picker stale และสิทธิ์ false ใน org ใหม่.
export type LinkAccountResult = {
  partner: {
    partnerId: string;
    externalId: string;
    linkedAt: string;
    publicUserId: string;
    publicOrganizationId: string;
  };
  organizationsRef: OrganizationsRef[];
  authorization: Authorization[];
};

// ไม่ใช้ handleApiError ตั้งใจ — หน้า SSO ต้องโชว์ error เป็น inline branded screen ไม่ใช่ modal alert.
const SsoService = {
  exchange: async (handoffCode: string): Promise<User> => {
    const response = await externalApiClient.post("/v1/app/sso/exchange", { handoffCode });
    return response.data.results.user;
  },

  // non-consuming — เรียกซ้ำได้ (StrictMode double-mount); token ถูก burn ตอน POST /link เท่านั้น.
  linkPreview: async (linkToken: string): Promise<LinkPreview> => {
    const response = await externalApiClient.get("/v1/app/sso/link/preview", {
      params: { linkToken },
    });
    return response.data.results;
  },

  // Bearer ใส่ตรงๆ (client นี้ส่วนใหญ่ unauth); organizationId ต้องเป็น numeric org.id ไม่ใช่ _id — login กรอง _id ออก; ไม่ส่ง orgChoice = backend ใช้ organizationsRef[0].
  linkAccount: async (
    linkToken: string,
    accessToken: string,
    orgChoice?: { organizationId: number } | { organization: LinkOrgInput },
  ): Promise<LinkAccountResult> => {
    const response = await externalApiClient.post(
      "/v1/app/sso/link",
      { linkToken, ...(orgChoice ?? {}) },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data.results;
  },

  // GET /v1/app/sso/first-bind/status?code=<fb_code> — no Bearer, fb_code is the credential.
  firstBindStatus: async (fbCode: string): Promise<FirstBindStatus> => {
    const response = await externalApiClient.get("/v1/app/sso/first-bind/status", {
      params: { code: fbCode },
    });
    return response.data.results;
  },

  // POST /v1/app/sso/first-bind/resend {code} — gated server-side by resend_available_at.
  firstBindResend: async (fbCode: string): Promise<FirstBindStatus> => {
    const response = await externalApiClient.post("/v1/app/sso/first-bind/resend", {
      code: fbCode,
    });
    return response.data.results;
  },

  // POST /v1/app/sso/first-bind/verify {code, otp} — success hands off to /auth/sso?code=.
  firstBindVerify: async (fbCode: string, otp: string): Promise<FirstBindVerifyResult> => {
    const response = await externalApiClient.post("/v1/app/sso/first-bind/verify", {
      code: fbCode,
      otp,
    });
    return response.data.results;
  },

  // GET /v1/app/sso/sectors — require_cc_session (Bearer main session), manual header like link/link-account.
  getOnboardingSectors: async (
    accessToken: string,
    taxonomy: SectorTaxonomy,
    level?: number,
    parentRef?: string,
  ): Promise<SectorOption[]> => {
    const response = await externalApiClient.get("/v1/app/sso/sectors", {
      params: { taxonomy, level, parentRef },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data.results.sectors;
  },

  // GET /v1/app/sso/onboarding?organizationRef=... — answers for one organisation, never for the person.
  getOnboardingStatus: async (
    accessToken: string,
    organizationRef: string,
  ): Promise<OnboardingResult> => {
    const response = await externalApiClient.get("/v1/app/sso/onboarding", {
      params: { organizationRef },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const status = response.data?.results?.onboarding_status;
    // A 200 in an unexpected shape would leave the caller undefined, which its gate reads as "still deciding" forever.
    if (status !== "pending" && status !== "completed") {
      throw new Error("unexpected onboarding status payload");
    }
    return response.data.results;
  },

  // POST /v1/app/sso/onboarding — one-shot per organisation; a second answer returns 409.
  submitOnboarding: async (
    accessToken: string,
    payload: OnboardingPayload,
  ): Promise<OnboardingResult> => {
    const response = await externalApiClient.post("/v1/app/sso/onboarding", payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data.results;
  },
};

export default SsoService;